import React, { useMemo, useEffect, useState, useCallback } from 'react';
import { Row, Col, Form, Input, Space } from 'antd';
import HorizontalAudiometryTable from './audiometry/HorizontalAudiometryTable';
import AudiometryChart from './audiometry/AudiometryChart';
import SymbologyTable from './audiometry/SymbologyTable';
import AudiometryAverages from './audiometry/AudiometryAverages';
import NormalForm from './NormalForm';
import { emitter } from '../../../../events';
import { useAudiometryChart } from '../../hooks/useAudiometryChart';
import {
  frequencies,
  bilateralAudiometryConfig,
} from './audiometry/audiometryConfig';

const parseValueNum = (value) => {
  const rawValue = String(value ?? '');
  const num = parseInt(rawValue.replace('no escucha', '').replace('NR', ''), 10);
  return isNaN(num) ? null : num;
};

const normalizeIAResult = (raw) => {
  const str = String(raw ?? '').trim();
  if (/^NR_M(\d+)$/.test(str)) return `${str.slice(4)} no escucha`;
  if (/^NR(\d+)$/.test(str)) return `${str.slice(2)} no escucha`;
  if (str === 'NR') return 'no escucha';
  if (/^M(\d+)$/.test(str)) return str.slice(1);
  return str;
};

const BilateralAudiometryForm = ({
  fromExternal,
  inputs,
  disableForm,
  form,
  chartRefRE,
  chartRefLE,
  audiometryState,
  setAudiometry,
  roles,
  fileList,
  loadingFiles,
  status,
  setFileList,
  toDoctor,
  removeDocument,
  convertFile,
  convertError,
  isDICOM,
  handleConvertFile,
  imageUrl,
  setImageUrl,
  setShowAudiometryChartRef,
  countrySettings,
}) => {
  const [chartData, setChartData] = useState([]);
  const [formInitialized, setFormInitialized] = useState(false);

  const { chartImage, chartImageRE, chartImageLE, showChart, clearChartImage } = useAudiometryChart(inputs);

  const thresholdProcedures = useMemo(() => {
    return (
      inputs?.procedures?.filter(
        (procedure) => ['thresholds', 'masking'].includes(procedure?.group)
      ) || []
    );
  }, [inputs?.procedures]);

  useEffect(() => {
    if (setShowAudiometryChartRef) {
      setShowAudiometryChartRef(showChart);
    }
  }, [setShowAudiometryChartRef, showChart]);

  const parseProcedureCode = (code) => {
    if (!code) return null;
    const isMasking = code.endsWith('_ENMASCARAMIENTO');
    const baseCode = isMasking ? code.replace('_ENMASCARAMIENTO', '') : code;
    const parts = baseCode.split('_');

    if (parts.length < 4) return null;

    const typeStr = parts[1];
    const sideStr = parts[2];
    const frequency = parts[3];

    const type = typeStr === 'UA' ? 'AC' : 'BC';
    const side = sideStr === 'OD' ? 'RE' : 'LE';

    const baseDataIndex = `${type}_${side}`;
    const dataIndex = isMasking ? `${baseDataIndex}_masking` : baseDataIndex;

    return { type, side, frequency, dataIndex, isMasking, baseDataIndex };
  };

  const procedureMap = useMemo(() => {
    const map = {};
    if (!thresholdProcedures) return map;

    thresholdProcedures.forEach((procedure) => {
      const parsed = parseProcedureCode(procedure.code);
      if (!parsed) return;

      const { frequency, dataIndex } = parsed;

      if (!map[frequency]) map[frequency] = {};
      map[frequency][dataIndex] = {
        id: procedure.id,
        result: procedure.result,
      };
    });

    return map;
  }, [thresholdProcedures]);

  const formValues = Form.useWatch([], form);

  const averageData = useMemo(() => {
    if (!formInitialized) return {};

    if (!fromExternal) {
      return frequencies.reduce((acc, frequency) => {
        acc[frequency] = formValues?.[frequency] || {};
        return acc;
      }, {});
    }

    const externalData = {};

    Object.entries(procedureMap).forEach(([frequency, procedureGroup]) => {
      externalData[frequency] = {};

      Object.entries(procedureGroup).forEach(([key, { result }]) => {
        if (!key.includes('_masking')) {
          externalData[frequency][key] = parseValueNum(result);
        }
      });
    });

    return externalData;
  }, [fromExternal, formValues, procedureMap, formInitialized]);

  const generateChartData = useCallback((values) => {
    return frequencies.map((freq) => {
      const row = values[freq] || {};

      const getMaskStatus = (thresh, mask) => {
        const str = String(thresh ?? '');
        if (str.toUpperCase() === 'NR' || str.includes('no escucha')) return 'no_hearing';
        return parseValueNum(mask) !== null ? 'masking' : 'none';
      };

      return {
        name: freq,
        AC_RE: parseValueNum(row.AC_RE),
        AC_LE: parseValueNum(row.AC_LE),
        BC_RE: parseValueNum(row.BC_RE),
        BC_LE: parseValueNum(row.BC_LE),
        AC_RE_masking: getMaskStatus(row.AC_RE, row.AC_RE_masking),
        AC_LE_masking: getMaskStatus(row.AC_LE, row.AC_LE_masking),
        BC_RE_masking: getMaskStatus(row.BC_RE, row.BC_RE_masking),
        BC_LE_masking: getMaskStatus(row.BC_LE, row.BC_LE_masking),
      };
    });
  }, []);

  useEffect(() => {
    if (formInitialized || !Object.keys(procedureMap).length) return;

    const initialFormValues = {};
    const initialProcedures = {};
    frequencies.forEach((freq) => {
      initialFormValues[freq] = {};
      const procedureGroup = procedureMap[freq];
      if (!procedureGroup) return;

      for (const dataIndex in procedureGroup) {
        const { id, result } = procedureGroup[dataIndex];
        initialProcedures[id] = result || '';
        initialFormValues[freq][dataIndex] = result;
      }
    });

    form.setFieldsValue({
      ...initialFormValues,
      procedures: initialProcedures,
    });
    setChartData(generateChartData(initialFormValues));
    setFormInitialized(true);
  }, [procedureMap, form, formInitialized, generateChartData]);

  const handleValueChange = (value, freq, dataIndex) => {
    clearChartImage();
    setTimeout(() => {
      const currentValues = form.getFieldsValue();
      const procedureId = procedureMap[freq]?.[dataIndex]?.id;
      if (!procedureId) return;

      const currentProcedures = form.getFieldValue('procedures') || {};

      form.setFieldsValue({
        procedures: {
          ...currentProcedures,
          [procedureId.toString()]: value,
        },
      });

      setAudiometry((currentState) => {
        const updatedProcedures = (
          currentState.procedures || inputs.procedures
        ).map((procedure) =>
          procedure.id === procedureId
            ? { ...procedure, result: value }
            : procedure,
        );
        return { ...currentState, procedures: updatedProcedures };
      });

      setChartData(generateChartData(currentValues));
    }, 0);
  };

  useEffect(() => {
    const IAResultsHandler = ({ data: results }) => {
      const formUpdate = {};
      const proceduresUpdate = {};

      results.forEach(({ code, result }) => {
        const parsed = parseProcedureCode(code);
        if (!parsed) return;
        const { frequency, dataIndex } = parsed;
        const procedureId = procedureMap[frequency]?.[dataIndex]?.id;
        if (!procedureId) return;

        const normalized = normalizeIAResult(result);
        formUpdate[frequency] = { ...formUpdate[frequency], [dataIndex]: normalized };
        proceduresUpdate[procedureId] = normalized;
      });

      if (!Object.keys(proceduresUpdate).length) return;

      clearChartImage();
      form.setFieldsValue({
        ...formUpdate,
        procedures: { ...form.getFieldValue('procedures'), ...proceduresUpdate },
      });

      setAudiometry((currentState) => ({
        ...currentState,
        procedures: (currentState.procedures || inputs.procedures).map((procedure) =>
          proceduresUpdate[procedure.id] !== undefined
            ? { ...procedure, result: proceduresUpdate[procedure.id] }
            : procedure
        ),
      }));

      setChartData(generateChartData(form.getFieldsValue()));
    };
    emitter.on('audiometryIAData', IAResultsHandler);
    return () => emitter.off('audiometryIAData', IAResultsHandler);
  }, [form, procedureMap]);

  const conclusionCodes = ['ESTADO_AUDIOMETRIA_BILATERAL', 'OBSERVACIONES_AUDIOMETRIA_BILATERAL'];
  const fileCodes = ['DOCUMENT_AUDI', 'IMAGE_AUDI'];

  const normalFormInputs = useMemo(
    () => ({
      ...inputs,
      procedures: inputs.procedures?.filter(
        (procedure) =>
          procedure.group !== 'thresholds' &&
          procedure.group !== 'masking' &&
          !fileCodes.includes(procedure.code) &&
          !conclusionCodes.includes(procedure.code)
      ),
    }),
    [inputs]
  );

  const conclusionInputs = useMemo(
    () => ({
      ...inputs,
      procedures: inputs.procedures?.filter((procedure) =>
        conclusionCodes.includes(procedure.code)
      ),
      measurements: [],
      questions: [],
      surveys: [],
    }),
    [inputs]
  );

  const fileInputs = useMemo(
    () => ({
      ...inputs,
      procedures: inputs.procedures?.filter((procedure) =>
        fileCodes.includes(procedure.code)
      ),
      measurements: [],
      questions: [],
      surveys: [],
    }),
    [inputs]
  );

  return (
    <Row justify='center' gutter={[16, 30]} className="separated-ears-form mt-20">
      <Col lg={24} xs={24}>
        <Row className='mt-20' justify='space-between'>
          <Col lg={fromExternal ? 24 : 10} className='col-container'>
            <Row justify='center'>
              <Col lg={22}>
                <h2>Resultados</h2>
              </Col>
            </Row>
            <Row className='results-row mt-20' />
            <Row className='mt-20' justify='center'>
              <Col lg={22}>
                <NormalForm
                  form={form}
                  inputs={normalFormInputs}
                  disableForm={disableForm}
                  roles={roles}
                  status={status}
                  loadingFiles={loadingFiles}
                  setFileList={setFileList}
                  fileList={fileList}
                  toDoctor={toDoctor}
                  removeDocument={removeDocument}
                  convertFile={convertFile}
                  convertError={convertError}
                  isDICOM={isDICOM}
                  handleConvertFile={handleConvertFile}
                  imageUrl={imageUrl}
                  setImageUrl={setImageUrl}
                  countrySettings={countrySettings}
                  fromExternal={fromExternal}
                />
              </Col>
            </Row>
          </Col>
        </Row>

        {!fromExternal && (
          <Row gutter={[16, 16]} className="section-margin-top">
            <Col lg={12} xs={24} className='col-container audiometry-results-container'>
              <Space direction='vertical' size='middle' style={{ width: '100%' }}>
                <h2 className="table-title">Valores Oído Derecho</h2>
                <HorizontalAudiometryTable
                  form={form}
                  sideType="RE"
                  onValueChange={handleValueChange}
                  disableForm={disableForm}
                />
              </Space>
            </Col>

            <Col lg={12} xs={24} className='col-container audiometry-results-container'>
              <Space direction='vertical' size='middle' style={{ width: '100%' }}>
                <h2 className="table-title">Valores Oído Izquierdo</h2>
                <HorizontalAudiometryTable
                  form={form}
                  sideType="LE"
                  onValueChange={handleValueChange}
                  disableForm={disableForm}
                />
              </Space>
            </Col>
          </Row>
        )}

        <Row className="section-margin-top" gutter={[16, 16]} align="stretch">
          <Col lg={fromExternal ? 24 : 6} xs={24}>
            <SymbologyTable mode="compact" />
          </Col>
          <Col lg={fromExternal ? 24 : 18} xs={24}>
            <Row gutter={[16, 16]}>
              <Col lg={12} xs={24} className='audiometry-chart-container'>
                <strong className="chart-title">Oído Derecho</strong>
                {!fromExternal ? (
                  <AudiometryChart
                    chartRef={chartRefRE}
                    data={chartData}
                    seriesConfig={bilateralAudiometryConfig.seriesRightEar}
                    compact={true}
                  />
                ) : (
                  chartImageRE ? <img src={chartImageRE} alt='OD' style={{ width: '100%' }} /> : (chartImage && <img src={chartImage} alt='Chart' style={{ width: '100%' }} />)
                )}
              </Col>
              <Col lg={12} xs={24} className='audiometry-chart-container'>
                <strong className="chart-title">Oído Izquierdo</strong>
                {!fromExternal ? (
                  <AudiometryChart
                    chartRef={chartRefLE}
                    data={chartData}
                    seriesConfig={bilateralAudiometryConfig.seriesLeftEar}
                    compact={true}
                  />
                ) : (
                  chartImageLE ? <img src={chartImageLE} alt='OI' style={{ width: '100%' }} /> : (chartImage && <img src={chartImage} alt='Chart' style={{ width: '100%' }} />)
                )}
              </Col>
            </Row>
          </Col>
        </Row>

        <Row className="averages-row">
          <Col lg={24} xs={24}>
            <AudiometryAverages
              averageData={averageData}
              config={bilateralAudiometryConfig.averages}
              fromExternal={fromExternal}
            />
          </Col>
        </Row>

        {thresholdProcedures.map((proc) => (
          <Form.Item
            key={proc.id}
            name={['procedures', proc.id.toString()]}
            noStyle
          >
            <Input type='hidden' />
          </Form.Item>
        ))}

        <Row gutter={[16, 16]} className="mt-20">
          <Col span={24}>
            <Col lg={24}>
              <h2 className="files-title">Archivos</h2>
            </Col>
            <Col
              lg={24}
              className={`col-container${!fromExternal ? ' audiometry-top-inputs' : ''
                }`}
            >
              <NormalForm
                inputs={fileInputs}
                setFileList={setFileList}
                fileList={fileList}
                loadingFiles={loadingFiles}
                toDoctor={toDoctor}
                disableForm={disableForm}
                roles={roles}
                status={status}
                form={form}
                removeDocument={removeDocument}
                convertFile={convertFile}
                convertError={convertError}
                isDICOM={isDICOM}
                handleConvertFile={handleConvertFile}
                imageUrl={imageUrl}
                setImageUrl={setImageUrl}
                countrySettings={countrySettings}
                fullInputs={inputs}
                fromExternal={fromExternal}
              />
            </Col>
          </Col>
        </Row>

        <Row justify='space-between' className="section-margin">
          <Col lg={fromExternal ? 24 : 10} className='col-container'>
            <Row justify='center'>
              <Col lg={22}>
                <h2>Conclusión</h2>
              </Col>
            </Row>
            <Row className='results-row mt-20' />
            <Row className='mt-20' justify='center'>
              <Col lg={22}>
                <NormalForm
                  inputs={conclusionInputs}
                  setFileList={setFileList}
                  fileList={fileList}
                  loadingFiles={loadingFiles}
                  toDoctor={toDoctor}
                  disableForm={disableForm}
                  roles={roles}
                  status={status}
                  form={form}
                  removeDocument={removeDocument}
                  convertFile={convertFile}
                  convertError={convertError}
                  isDICOM={isDICOM}
                  handleConvertFile={handleConvertFile}
                  imageUrl={imageUrl}
                  setImageUrl={setImageUrl}
                  countrySettings={countrySettings}
                  fullInputs={inputs}
                  fromExternal={fromExternal}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default BilateralAudiometryForm;
